import { useEffect, useState } from "react";
import { List, Bookmark as BookmarkIcon, StickyNote, Search as SearchIcon, X, Type, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { Slider } from "./ui/slider";
import { Input } from "./ui/input";
import { toast } from "sonner";

/**
 * ReaderDrawer bundles four side panels controlled by their icon triggers:
 *  - TOC (chapters)
 *  - Bookmarks
 *  - Notes for this book
 *  - Settings (font size, line height, page width)
 *  - Search inside book
 */
export function ChaptersButton({ chapters, currentPage, onJump }) {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          data-testid="open-toc-btn"
          className="p-2 rounded-full hover:bg-secondary transition-colors"
          title="table of contents"
        >
          <List className="w-4 h-4" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[340px] sm:w-[400px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl lowercase tracking-tight">table of contents</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-1 font-ui text-sm">
          {chapters.map((ch, i) => {
            const active = i === chapters.length - 1
              ? currentPage >= ch.page
              : currentPage >= ch.page && currentPage < chapters[i + 1].page;
            return (
              <button
                key={`${ch.title}-${ch.page}-${i}`}
                onClick={() => onJump(ch.page)}
                data-testid={`toc-item-${i}`}
                className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                  active ? "bg-primary/10 text-primary" : "hover:bg-secondary"
                }`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate">{ch.title}</span>
                  <span className="font-ui text-xs text-muted-foreground shrink-0">p.{ch.page + 1}</span>
                </div>
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function BookmarksButton({ bookId, currentPage, onJump }) {
  const [items, setItems] = useState([]);
  const load = () => {
    api.get("/bookmarks", { params: { book_id: bookId } })
      .then((r) => setItems(r.data.bookmarks || []))
      .catch(() => setItems([]));
  };
  const del = async (id) => {
    try {
      await api.delete(`/bookmarks/${id}`);
      setItems((v) => v.filter((x) => x.id !== id));
      toast.success("bookmark removed");
    } catch { /* ignore */ }
  };
  return (
    <Sheet onOpenChange={(o) => o && load()}>
      <SheetTrigger asChild>
        <button
          data-testid="open-bookmarks-btn"
          className="p-2 rounded-full hover:bg-secondary transition-colors"
          title="bookmarks"
        >
          <BookmarkIcon className="w-4 h-4" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[340px] sm:w-[400px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl lowercase tracking-tight">bookmarks</SheetTitle>
        </SheetHeader>
        {items.length === 0 && (
          <div className="mt-6 font-reader italic text-sm text-muted-foreground">
            no bookmarks yet. tap the bookmark icon on any page to save your place.
          </div>
        )}
        <div className="mt-4 space-y-2">
          {items.map((bm) => (
            <div
              key={bm.id}
              data-testid={`bookmark-item-${bm.id}`}
              className="group flex items-center gap-2 px-3 py-2 rounded-md hover:bg-secondary"
            >
              <button
                onClick={() => onJump(bm.page)}
                className="flex-1 text-left"
              >
                <div className="font-display text-base">page {bm.page + 1}</div>
                <div className="font-ui text-xs text-muted-foreground">
                  {new Date(bm.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                </div>
              </button>
              <button
                onClick={() => del(bm.id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 hover:text-destructive rounded-full"
                title="remove"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function BookNotesButton({ bookId, onJump }) {
  const [items, setItems] = useState([]);
  const load = () => {
    api.get("/notes", { params: { book_id: bookId } })
      .then((r) => setItems(r.data.notes || []))
      .catch(() => setItems([]));
  };
  const del = async (id) => {
    try {
      await api.delete(`/notes/${id}`);
      setItems((v) => v.filter((x) => x.id !== id));
      toast.success("note deleted");
    } catch { /* ignore */ }
  };
  return (
    <Sheet onOpenChange={(o) => o && load()}>
      <SheetTrigger asChild>
        <button
          data-testid="open-notes-btn"
          className="p-2 rounded-full hover:bg-secondary transition-colors"
          title="notes for this book"
        >
          <StickyNote className="w-4 h-4" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[380px] sm:w-[440px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl lowercase tracking-tight">notes on this book</SheetTitle>
        </SheetHeader>
        {items.length === 0 && (
          <div className="mt-6 font-reader italic text-sm text-muted-foreground">
            no notes for this book yet. select any passage while reading to save a thought.
          </div>
        )}
        <div className="mt-4 space-y-3">
          {items.map((n) => (
            <div
              key={n.id}
              data-testid={`book-note-${n.id}`}
              className="group border border-border rounded-md p-3 hover:bg-secondary/60"
            >
              {n.excerpt && (
                <div className="font-reader italic text-sm border-l-2 border-primary/50 pl-3 mb-2 line-clamp-3">
                  &ldquo;{n.excerpt}&rdquo;
                </div>
              )}
              <div className="font-reader text-sm whitespace-pre-wrap">{n.body}</div>
              <div className="mt-2 flex items-center justify-between font-ui text-[10px] tracking-caps text-muted-foreground">
                <button onClick={() => n.page != null && onJump(n.page)} className="hover:text-primary">
                  page {n.page != null ? n.page + 1 : "—"}
                </button>
                <button onClick={() => del(n.id)} className="opacity-0 group-hover:opacity-100 hover:text-destructive">
                  delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function ReaderSettingsButton({ settings, setSettings }) {
  const upd = (k, v) => setSettings({ ...settings, [k]: v });
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          data-testid="open-settings-btn"
          className="p-2 rounded-full hover:bg-secondary transition-colors"
          title="reader settings"
        >
          <Type className="w-4 h-4" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[340px]">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl lowercase tracking-tight">reading settings</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-8 font-ui">
          <div>
            <div className="flex justify-between text-xs tracking-caps text-muted-foreground mb-2">
              <span>font size</span><span>{settings.fontSize}px</span>
            </div>
            <Slider
              min={14} max={28} step={1}
              value={[settings.fontSize]}
              onValueChange={(v) => upd("fontSize", v[0])}
              data-testid="settings-font-size"
            />
          </div>
          <div>
            <div className="flex justify-between text-xs tracking-caps text-muted-foreground mb-2">
              <span>line height</span><span>{settings.lineHeight.toFixed(2)}</span>
            </div>
            <Slider
              min={1.4} max={2.2} step={0.05}
              value={[settings.lineHeight]}
              onValueChange={(v) => upd("lineHeight", v[0])}
              data-testid="settings-line-height"
            />
          </div>
          <div>
            <div className="flex justify-between text-xs tracking-caps text-muted-foreground mb-2">
              <span>page width</span><span>{settings.maxWidth}px</span>
            </div>
            <Slider
              min={520} max={900} step={10}
              value={[settings.maxWidth]}
              onValueChange={(v) => upd("maxWidth", v[0])}
              data-testid="settings-page-width"
            />
          </div>
          <div>
            <div className="text-xs tracking-caps text-muted-foreground mb-2">typeface</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "'Spectral', serif", l: "spectral" },
                { v: "'Cormorant Garamond', serif", l: "cormorant" },
                { v: "'Outfit', sans-serif", l: "outfit sans" },
                { v: "Georgia, serif", l: "georgia" },
              ].map((o) => (
                <button
                  key={o.v}
                  onClick={() => upd("fontFamily", o.v)}
                  data-testid={`settings-typeface-${o.l.replace(/\s+/g, "-")}`}
                  className={`px-3 py-2 rounded-md border text-sm transition-colors ${
                    settings.fontFamily === o.v ? "border-primary text-primary" : "border-border hover:bg-secondary"
                  }`}
                  style={{ fontFamily: o.v }}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function SearchInsideButton({ bookId, onJump }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    if (q.trim().length < 2) return;
    setLoading(true);
    try {
      const r = await api.get(`/books/${bookId}/search`, { params: { q } });
      setHits(r.data.hits || []);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Sheet onOpenChange={(o) => !o && setHits([])}>
      <SheetTrigger asChild>
        <button
          data-testid="open-search-btn"
          className="p-2 rounded-full hover:bg-secondary transition-colors"
          title="search inside book"
        >
          <SearchIcon className="w-4 h-4" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[380px] sm:w-[440px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl lowercase tracking-tight">search inside</SheetTitle>
        </SheetHeader>
        <form onSubmit={(e) => { e.preventDefault(); run(); }} className="mt-4">
          <Input
            data-testid="search-inside-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="search this book…"
            className="font-ui"
            autoFocus
          />
        </form>
        <div className="mt-4 space-y-2 font-ui text-sm">
          {loading && <div className="text-muted-foreground italic">searching…</div>}
          {!loading && hits.length === 0 && q && <div className="text-muted-foreground italic">no matches.</div>}
          {hits.map((h, i) => (
            <button
              key={i}
              onClick={() => onJump(h.page)}
              data-testid={`search-hit-${i}`}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-secondary transition-colors"
            >
              <div className="font-display text-xs tracking-caps text-primary mb-1">page {h.page + 1}</div>
              <div className="font-reader text-sm text-muted-foreground line-clamp-2">{h.snippet}</div>
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
